/**
 * VariantDiff — side-by-side "what the optimizer changed" view (UI-SPEC §3.4):
 * the incumbent vs candidate prompt body, with a per-line add/remove gutter and
 * an eval scoreboard. This is how a human audits a mutation before gating it.
 */

import * as React from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../card";

export interface DiffScoreboard {
  incumbent_tokens: number;
  candidate_tokens: number;
  delta_pct: number;
  correctness_incumbent?: number | null;
  correctness_candidate?: number | null;
}

// Line-level diff: a line present in both is "common"; only-in-incumbent is
// "removed"; only-in-candidate is "added". Set-based (not LCS) — legible for a
// prompt audit without a heavy diff dependency.
function diffLines(a: string, b: string) {
  const aLines = a.split("\n");
  const bLines = b.split("\n");
  const bSet = new Set(bLines);
  const aSet = new Set(aLines);
  return {
    incumbent: aLines.map((l) => ({ line: l, removed: !bSet.has(l) })),
    candidate: bLines.map((l) => ({ line: l, added: !aSet.has(l) })),
  };
}

export function VariantDiff({
  incumbentPrompt,
  candidatePrompt,
  scoreboard,
  label = "Active vs candidate",
}: {
  incumbentPrompt: string;
  candidatePrompt: string;
  scoreboard?: DiffScoreboard;
  label?: string;
}) {
  const d = diffLines(incumbentPrompt, candidatePrompt);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{label}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {scoreboard && (
          <div className="flex flex-wrap gap-4 type-body tabular-nums">
            <span className="text-muted-foreground">
              tokens {scoreboard.incumbent_tokens} →{" "}
              <span className="text-foreground">{scoreboard.candidate_tokens}</span>{" "}
              <span className={scoreboard.delta_pct < 0 ? "text-success" : "text-destructive"}>
                ({scoreboard.delta_pct > 0 ? "+" : ""}
                {scoreboard.delta_pct.toFixed(0)}%)
              </span>
            </span>
            {scoreboard.correctness_candidate != null && (
              <span className="text-muted-foreground">
                correctness {scoreboard.correctness_incumbent ?? "—"} →{" "}
                <span className="text-foreground">{scoreboard.correctness_candidate}</span>
              </span>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 @lg/page:grid-cols-2">
          <div>
            <p className="mb-1 type-subtext text-muted-foreground">incumbent</p>
            <pre className="max-h-80 overflow-auto rounded bg-selected p-2 type-subtext leading-body">
              {d.incumbent.map((r, i) => (
                <div key={i} className={r.removed ? "bg-destructive/10 text-destructive" : ""}>
                  {r.removed ? "− " : "  "}
                  {r.line}
                </div>
              ))}
            </pre>
          </div>
          <div>
            <p className="mb-1 type-subtext text-muted-foreground">candidate</p>
            <pre className="max-h-80 overflow-auto rounded bg-selected p-2 type-subtext leading-body">
              {d.candidate.map((r, i) => (
                <div key={i} className={r.added ? "bg-success/10 text-success" : ""}>
                  {r.added ? "+ " : "  "}
                  {r.line}
                </div>
              ))}
            </pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
